import { action, computed, makeObservable, observable } from 'mobx';
import { IEntity } from '../shared/interfaces/entity.interface';
import { RootStore } from './RootStore';

// gap - it's a gap between calls 500ms, after that call next
type StatusDialerType = 'onCall' | 'onPause' | 'onGap';

export type StatusCalleeType = 'active' | 'pending' | 'completed';

export interface ICallee {
  user: IEntity;
  status: StatusCalleeType;
}

class DialerStore {
  @observable
  activeDialer: ICallee[];

  @observable
  statusDialer: StatusDialerType;

  @computed
  get firstPending(): ICallee {
    return this.activeDialer.find((c) => c.status === 'pending');
  }

  @computed
  get activeCallee(): ICallee {
    return this.activeDialer.find((c) => c.status === 'active');
  }

  @computed
  get isPendingCallee(): boolean {
    return this.activeDialer.some((c) => c.status === 'pending');
  }

  @computed
  get isDialerActive(): boolean {
    return (
      this.activeDialer.filter((c) => ['pending', 'active'].includes(c.status))
        ?.length > 0
    );
  }

  @computed
  get numberOfCallee(): number {
    return this.activeDialer.filter((c) =>
      ['pending', 'active'].includes(c.status),
    )?.length;
  }

  @action
  switchToAnotherCallee = () => {
    if (this.statusDialer === 'onPause') {
      return;
    }

    const prevStatus = this.statusDialer;
    this.statusDialer = 'onGap';

    setTimeout(() => {
      if (this.statusDialer === 'onPause') {
        return;
      }
      this.statusDialer = prevStatus;
    }, this.root.settingsStore.dialer.wrapUpTime);
  };

  @action
  setActiveDialerList = (users: IEntity[]) => {
    this.activeDialer = users.map((u) => {
      return { user: u, status: 'pending' };
    });
  };

  @action
  removePendingCallee = () => {
    this.activeDialer = this.activeDialer.filter((c) => c.status !== 'pending');
  };

  @action
  endDialer = () => {
    this.activeDialer = [];
  };

  @action
  changeCalleeStatus = (status: StatusCalleeType, user: IEntity) => {
    this.activeDialer = this.activeDialer.map((c) => {
      if (c.user.id === user.id) {
        return { user, status };
      }

      return c;
    });
  };

  @action
  removeCallee = (user: IEntity) => {
    this.activeDialer = this.activeDialer.filter((c) => c.user.id !== user.id);
  };

  @action
  removeActiveCallee = () => {
    this.activeDialer = this.activeDialer.map((c) => {
      if (c.status === 'active') {
        return { ...c, status: 'completed' };
      }

      return c;
    });
  };

  @action
  setActiveCallee = (user: IEntity) => {
    this.changeCalleeStatus('active', user);
  };

  @action
  toggleDialler = () => {
    if (!this.statusDialer) {
      this.statusDialer = 'onCall';
      return;
    }

    if (this.statusDialer === 'onCall' || this.statusDialer === 'onGap') {
      this.statusDialer = 'onPause';
      return;
    }

    if (this.statusDialer === 'onPause') {
      this.statusDialer = 'onCall';
    }
  };

  constructor(public root: RootStore) {
    // mockData
    // this.statusDialer = 'onPause';
    // this.activeDialer = [
    //     {
    //         status: 'completed',
    //         user: { ...COMMON.UNKNOWN_ENTITY, id: '1', phones: ['123456'] },
    //     }, {
    //         status: 'active',
    //         user: { ...COMMON.UNKNOWN_ENTITY, phones: ['123456'], id: '2' },
    //     }, {
    //         status: 'pending',
    //         user: { ...COMMON.UNKNOWN_ENTITY, id: '3', phones: ['123456'] },
    //     }, {
    //         status: 'pending',
    //         user: { ...COMMON.UNKNOWN_ENTITY, id: '4', phones: ['123456'] },
    //     }, {
    //         status: 'pending',
    //         user: { ...COMMON.UNKNOWN_ENTITY, id: '5', phones: ['123456'] },
    //     },
    // ];
    this.activeDialer = [];

    makeObservable(this);
  }
}

export default DialerStore;
